import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

export interface Form4811PDFData {
  // ── Exact CBP Form 4811 (12/25) fields ──
  // Section 1: Request Code
  requestCode?: string;
  // Section 2: Agent (pre-filled)
  agentLastName?: string;
  agentFirstName?: string;
  agentMiddleInitial?: string;
  agentNumber?: string;
  agentAddress?: string;
  // Section 3: Importer of Record
  importerName: string;
  importerNumber?: string;
  // Section 4: Signature
  electronicSignature: string;
  signerTitle?: string;
  effectiveDate?: string;
  // Meta
  clientName: string;
  clientEmail: string;
  submittedAt: string;
  // Legacy compat fields (kept so existing saved PDFs still work)
  importerEin?: string;
  importerPhone?: string;
  importerCountry?: string;
  importerAddress?: string;
  importerCity?: string;
  importerState?: string;
  importerZip?: string;
  dateOfLiquidation?: string;
  requestType?: string;
  capacity?: string;
}

const GOLD = rgb(0.992, 0.714, 0.075); // #FDB913
const DARK = rgb(0.051, 0.082, 0.125); // #0d1520
const WHITE = rgb(1, 1, 1);
const GRAY = rgb(0.6, 0.6, 0.6);
const BLACK = rgb(0, 0, 0);
const LIGHT_GRAY = rgb(0.95, 0.95, 0.95);

function drawRect(
  page: ReturnType<PDFDocument['addPage']>,
  x: number, y: number, w: number, h: number,
  color: ReturnType<typeof rgb>,
  opacity = 1,
) {
  page.drawRectangle({ x, y, width: w, height: h, color, opacity });
}

function drawText(
  page: ReturnType<PDFDocument['addPage']>,
  text: string,
  x: number, y: number,
  size: number,
  color: ReturnType<typeof rgb>,
  font: Awaited<ReturnType<PDFDocument['embedFont']>>,
) {
  if (!text) return;
  page.drawText(text, { x, y, size, color, font });
}

function drawLine(
  page: ReturnType<PDFDocument['addPage']>,
  x1: number, y1: number, x2: number, y2: number,
  color: ReturnType<typeof rgb>,
  thickness = 0.5,
) {
  page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness, color });
}

function truncate(str: string | undefined, maxLen: number): string {
  if (!str) return '';
  return str.length > maxLen ? `${str.slice(0, maxLen - 1)}…` : str;
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

export async function generateForm4811PDF(data: Form4811PDFData): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();
  const pageWidth = 612;
  const pageHeight = 792;
  const page = pdfDoc.addPage([pageWidth, pageHeight]);

  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const italicFont = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

  const margin = 40;
  const contentWidth = pageWidth - margin * 2;

  // ── HEADER BANNER ──────────────────────────────────────────────────────────
  drawRect(page, 0, pageHeight - 80, pageWidth, 80, DARK);
  drawRect(page, 0, pageHeight - 82, pageWidth, 2, GOLD);

  drawText(page, 'DEPARTMENT OF HOMELAND SECURITY', margin, pageHeight - 22, 7, GOLD, boldFont);
  drawText(page, 'U.S. Customs and Border Protection', margin, pageHeight - 34, 9, WHITE, regularFont);
  drawText(page, 'SPECIAL ADDRESS NOTIFICATION', margin, pageHeight - 52, 14, WHITE, boldFont);
  drawText(page, 'CBP Form 4811 — Designation of Agent for Electronic ACH Refund Receipt', margin, pageHeight - 66, 7.5, GRAY, regularFont);

  // OMB number top right
  drawText(page, 'OMB No. 1651-0076', pageWidth - margin - 90, pageHeight - 22, 7, GRAY, regularFont);
  drawText(page, 'ESIGN Act Compliant', pageWidth - margin - 90, pageHeight - 34, 7, GOLD, boldFont);

  let y = pageHeight - 100;

  // ── SUBMISSION INFO BAR ────────────────────────────────────────────────────
  drawRect(page, margin, y - 22, contentWidth, 26, LIGHT_GRAY);
  drawText(page, `Submitted: ${formatDate(data.submittedAt)}`, margin + 8, y - 16, 8, BLACK, regularFont);
  drawText(page, `Client: ${truncate(data.clientName, 40)}`, margin + 180, y - 16, 8, BLACK, regularFont);
  drawText(page, `Email: ${truncate(data.clientEmail, 40)}`, margin + 360, y - 16, 8, BLACK, regularFont);
  y -= 38;

  // ── SECTION 1: REQUEST CODE (all 7 codes from real CBP Form 4811 12/25) ───
  drawRect(page, margin, y - 18, contentWidth, 20, DARK);
  drawText(page, '  SELECT REQUEST CODE \u2014 Check Only One Box', margin, y - 13, 8.5, GOLD, boldFont);
  y -= 28;

  const selectedCode = data.requestCode || data.requestType || '10';
  const REQUEST_CODES_PDF = [
    { code: '10', desc: 'Bills, Refunds and Notices of Liquidation' },
    { code: '20', desc: 'Refunds and Drawbacks Only' },
    { code: '23', desc: 'Bills and Refunds' },
    { code: '24', desc: 'Notices of Liquidation Only' },
    { code: '30', desc: 'Bills Only' },
    { code: '34', desc: 'Refunds and Notices' },
    { code: '40', desc: 'Bills and Notices' },
  ];

  const colW = contentWidth / 2;
  REQUEST_CODES_PDF.forEach((opt, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const cx = margin + col * (colW + 2);
    const cy = y - row * 26;
    const isSelected = selectedCode === opt.code;
    // Checkbox
    drawRect(page, cx + 4, cy - 16, 10, 10, WHITE);
    page.drawRectangle({ x: cx + 4, y: cy - 16, width: 10, height: 10, borderColor: BLACK, borderWidth: 1, color: WHITE });
    if (isSelected) {
      page.drawLine({ start: { x: cx + 5, y: cy - 12 }, end: { x: cx + 8, y: cy - 16 }, thickness: 1.5, color: BLACK });
      page.drawLine({ start: { x: cx + 8, y: cy - 16 }, end: { x: cx + 13, y: cy - 8 }, thickness: 1.5, color: BLACK });
    }
    drawText(page, `Code ${opt.code}`, cx + 18, cy - 8, 8, isSelected ? BLACK : GRAY, isSelected ? boldFont : regularFont);
    drawText(page, opt.desc, cx + 18, cy - 18, 6.5, GRAY, regularFont);
  });
  y -= Math.ceil(REQUEST_CODES_PDF.length / 2) * 26 + 10;

  drawLine(page, margin, y, margin + contentWidth, y, GRAY, 0.5);
  y -= 12;

  // ── SECTION 2: AGENT INFORMATION ─────────────────────────────────────────
  drawRect(page, margin, y - 18, contentWidth, 20, DARK);
  drawText(page, "  SECTION 2 \u2014 AGENT'S NAME AND NUMBER (Notify Party)", margin, y - 13, 8.5, GOLD, boldFont);
  y -= 28;

  const agentLast = data.agentLastName || 'Thabit';
  const agentFirst = data.agentFirstName || 'Bruce';
  const agentMI = data.agentMiddleInitial || 'A';
  const agentNum = data.agentNumber || '***-**-****';
  const agentAddr = data.agentAddress || '1037 NE 65th St #80300, Seattle, WA 98115';

  drawRect(page, margin, y - 46, contentWidth, 52, rgb(0.98, 0.97, 0.93));
  drawRect(page, margin, y - 46, 3, 52, GOLD);

  const col2 = contentWidth / 2;
  drawText(page, "Agent's Last Name:", margin + 8, y - 4, 7, GRAY, boldFont);
  drawText(page, agentLast, margin + 8, y - 16, 10, BLACK, boldFont);

  drawText(page, 'First Name / Middle Initial:', margin + 8 + col2 / 2, y - 4, 7, GRAY, boldFont);
  drawText(page, `${agentFirst} ${agentMI}.`, margin + 8 + col2 / 2, y - 16, 10, BLACK, boldFont);

  drawText(page, "Agent's Importer Number (with hyphens):", margin + 8, y - 28, 7, GRAY, boldFont);
  drawText(page, agentNum, margin + 8, y - 40, 10, BLACK, boldFont);

  drawText(page, "Agent's Address:", margin + 8 + col2 / 2, y - 28, 7, GRAY, boldFont);
  drawText(page, truncate(agentAddr, 45), margin + 8 + col2 / 2, y - 40, 8.5, BLACK, regularFont);
  y -= 60;

  drawLine(page, margin, y, margin + contentWidth, y, GRAY, 0.5);
  y -= 12;

  // ── SECTION 3: MY (ORGANIZATION'S) IMPORTER NUMBER ───────────────────────
  drawRect(page, margin, y - 18, contentWidth, 20, DARK);
  drawText(page, "  SECTION 3 \u2014 MY (ORGANIZATION'S) IMPORTER NUMBER (Show hyphens):", margin, y - 13, 8.5, GOLD, boldFont);
  y -= 28;

  drawRect(page, margin, y - 32, contentWidth, 36, rgb(0.98, 0.97, 0.93));
  drawRect(page, margin, y - 32, 3, 36, GOLD);
  drawText(page, "My (Organization's) Importer Number (Show hyphens):", margin + 8, y - 6, 7.5, GRAY, boldFont);
  drawText(page, truncate(data.importerNumber || 'Not provided', 60), margin + 8, y - 20, 11, BLACK, boldFont);
  drawText(page, 'Must have a CBP Form 5106 on file. EIN: XX-XXXXXXX | SSN: XXX-XX-XXXX | CBP-assigned number', margin + 8, y - 30, 6.5, GRAY, regularFont);
  y -= 44;

  drawLine(page, margin, y, margin + contentWidth, y, GRAY, 0.5);
  y -= 12;

  // ── SECTION 4: SIGNATURE, TITLE AND DATE ─────────────────────────────────
  drawRect(page, margin, y - 18, contentWidth, 20, DARK);
  drawText(page, '  SECTION 4 \u2014 SIGNATURE, TITLE AND DATE  (ESIGN Act, 15 U.S.C. \u00a7 7001)', margin, y - 13, 8.5, GOLD, boldFont);
  y -= 28;

  // Signature + Date row
  drawRect(page, margin, y - 46, contentWidth, 50, rgb(0.98, 0.97, 0.93));
  drawRect(page, margin, y - 46, 3, 50, GOLD);

  const sigColW = contentWidth * 0.65;
  drawText(page, 'Electronic Signature (Typed Full Legal Name):', margin + 8, y - 6, 7.5, GRAY, boldFont);
  drawLine(page, margin + 8, y - 28, margin + sigColW - 4, y - 28, BLACK, 0.8);
  drawText(page, truncate(data.electronicSignature, 45), margin + 8, y - 24, 13, BLACK, italicFont);

  drawText(page, 'Date:', margin + sigColW + 8, y - 6, 7.5, GRAY, boldFont);
  drawText(page, formatDate(data.submittedAt), margin + sigColW + 8, y - 18, 9, BLACK, boldFont);
  y -= 58;

  // Capacity checkboxes — exact CBP Form 4811 layout
  drawRect(page, margin, y - 56, contentWidth, 60, rgb(0.97, 0.97, 0.97));
  drawRect(page, margin, y - 56, 3, 60, GOLD);
  drawText(page, 'CAPACITY \u2014 Check One:', margin + 8, y - 6, 7.5, GRAY, boldFont);

  const title = data.signerTitle || '';
  const isPrincipal = title === 'Principal';
  const isMemberOfFirm = title === 'Member of Firm';
  const isCorpTitle = !isPrincipal && !isMemberOfFirm;

  // Principal checkbox
  page.drawRectangle({ x: margin + 8, y: y - 22, width: 9, height: 9, borderColor: BLACK, borderWidth: 1, color: WHITE });
  if (isPrincipal) {
    page.drawLine({ start: { x: margin + 9, y: y - 18 }, end: { x: margin + 12, y: y - 22 }, thickness: 1.5, color: BLACK });
    page.drawLine({ start: { x: margin + 12, y: y - 22 }, end: { x: margin + 16, y: y - 14 }, thickness: 1.5, color: BLACK });
  }
  drawText(page, 'Principal', margin + 22, y - 16, 9, isPrincipal ? BLACK : GRAY, isPrincipal ? boldFont : regularFont);

  // Member of Firm checkbox
  page.drawRectangle({ x: margin + 90, y: y - 22, width: 9, height: 9, borderColor: BLACK, borderWidth: 1, color: WHITE });
  if (isMemberOfFirm) {
    page.drawLine({ start: { x: margin + 91, y: y - 18 }, end: { x: margin + 94, y: y - 22 }, thickness: 1.5, color: BLACK });
    page.drawLine({ start: { x: margin + 94, y: y - 22 }, end: { x: margin + 98, y: y - 14 }, thickness: 1.5, color: BLACK });
  }
  drawText(page, 'Member of Firm', margin + 104, y - 16, 9, isMemberOfFirm ? BLACK : GRAY, isMemberOfFirm ? boldFont : regularFont);

  // _____ of the Corp. (Title) checkbox
  page.drawRectangle({ x: margin + 8, y: y - 44, width: 9, height: 9, borderColor: BLACK, borderWidth: 1, color: WHITE });
  if (isCorpTitle) {
    page.drawLine({ start: { x: margin + 9, y: y - 40 }, end: { x: margin + 12, y: y - 44 }, thickness: 1.5, color: BLACK });
    page.drawLine({ start: { x: margin + 12, y: y - 44 }, end: { x: margin + 16, y: y - 36 }, thickness: 1.5, color: BLACK });
  }
  // Underline for corp title
  drawLine(page, margin + 22, y - 36, margin + 200, y - 36, BLACK, 0.5);
  drawText(page, truncate(isCorpTitle ? title : '', 22), margin + 22, y - 38, 9, BLACK, isCorpTitle ? boldFont : regularFont);
  drawText(page, 'of the Corp. (Title)', margin + 205, y - 38, 9, isCorpTitle ? BLACK : GRAY, isCorpTitle ? boldFont : regularFont);
  y -= 68;

  // Certification text
  drawRect(page, margin, y - 36, contentWidth, 40, LIGHT_GRAY);
  drawText(page, 'CERTIFICATION: By signing above, the undersigned certifies that the information provided is true and accurate and authorizes', margin + 6, y - 10, 7, BLACK, regularFont);
  drawText(page, 'Attorney Bruce A. Thabit / Serenity Law PLLC to receive electronic ACH refund payments from U.S. Customs and Border Protection', margin + 6, y - 20, 7, BLACK, regularFont);
  drawText(page, 'on behalf of the Importer of Record. This constitutes a legally binding electronic signature under the ESIGN Act (15 U.S.C. § 7001).', margin + 6, y - 30, 7, BLACK, regularFont);
  y -= 48;

  // ── FOOTER ─────────────────────────────────────────────────────────────────
  drawLine(page, margin, 55, margin + contentWidth, 55, GOLD, 0.8);
  drawRect(page, 0, 0, pageWidth, 52, DARK);
  drawText(page, 'Serenity Law PLLC  |  Attorney Bruce A. Thabit  |  1037 NE 65th St #80300, Seattle, WA 98115', margin, 34, 7, GRAY, regularFont);
  drawText(page, 'tariffrefund.attorney  |  CBP Form 4811 — Special Address Notification', margin, 22, 7, GRAY, regularFont);
  drawText(page, `Generated: ${new Date().toLocaleString()}`, margin, 10, 6.5, rgb(0.4, 0.4, 0.4), regularFont);
  drawText(page, 'ESIGN Act Compliant Electronic Record', pageWidth - margin - 160, 10, 6.5, GOLD, boldFont);

  const pdfBytes = await pdfDoc.save();
  return pdfBytes;
}

export function downloadPDF(bytes: Uint8Array, filename: string) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
